import { Button, DatePicker, Form, Input, Radio, Select, Tag } from 'antd'
import moment from 'moment'
import React from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useMediaQuery } from 'react-responsive'
import styled from 'styled-components'
import { userApi } from '../../../../../apis'
import {
  ActionEnum,
  AlertMessageEnum,
  getDateFormat,
} from '../../../../../common'
import {
  ManageModalComponent,
  ManageModalFooterComponent,
} from '../../../../../components'
import { translate } from '../../../../../languages'
import { UserEntity, UserTypeEnum } from '../../../../../models'
import { RootState } from '../../../../../stores'
import {
  loadEmployees,
  loadProfile,
  loadStations,
  setAction,
  setActionProcessing,
  setAlert,
  setSelectedUser,
  setShowManageModal,
  setShowResetPasswordModal,
  setUsers,
} from '../../../../../stores/actions'

const StyledFormItem = styled(Form.Item)`
  margin-top: 5px;
`

const StyledLabel = styled.span`
  font-weight: bold;
`

type InputUser = {
  name: string
  address: string
  phone: string
  identityCardNumber: string
  gender: boolean
  dateOfBirth: Date
  userTypeId: number
  username: string
  password: string
  email?: string
  note?: string
}

export const ManageUserModalComponent: React.FC = () => {
  const dispatch = useDispatch()

  const [form] = Form.useForm()
  const { selectedUser, users, userTypes, isUserTypesLoaded } = useSelector(
    (state: RootState) => state.user
  )
  const { profile } = useSelector((state: RootState) => state.auth)
  const { isEmployeesLoaded } = useSelector(
    (state: RootState) => state.employee
  )
  const { management, regex } = useSelector((state: RootState) => state.common)
  const { isStationsLoaded } = useSelector((state: RootState) => state.station)
  const { language } = useSelector((state: RootState) => state.language)

  const isDesktopScreen = useMediaQuery({ minWidth: '1300px' })

  const readOnlyMode = React.useMemo(
    () => [ActionEnum.VIEW, ActionEnum.DELETE].includes(management.action),
    [management]
  )

  const replaceUsers = (user: UserEntity) => {
    const index = users.map((item) => item.id).indexOf(user.id)
    if (index > -1) {
      dispatch(
        setUsers([...users.slice(0, index), user, ...users.slice(index + 1)])
      )
    }
  }

  const onFinish = async (value: InputUser) => {
    dispatch(setAlert())
    dispatch(setActionProcessing(true))
    switch (management.action) {
      case ActionEnum.VIEW:
        dispatch(setAction(ActionEnum.UPDATE))
        break
      case ActionEnum.UPDATE:
        if (selectedUser) {
          try {
            const respUpdate = await userApi.updateUser(selectedUser.id, {
              ...value,
              dateOfBirth: new Date(
                value.dateOfBirth || selectedUser.dateOfBirth
              ).getTime(),
              email:
                value.email && value.email.trim().length
                  ? value.email
                  : undefined,
            })
            replaceUsers(respUpdate.data)
            if (selectedUser.name !== value.name && isStationsLoaded) {
              dispatch(loadStations())
              if (
                selectedUser.userType.type === UserTypeEnum.OWNER &&
                isEmployeesLoaded
              ) {
                dispatch(loadEmployees())
              }
            }
            dispatch(setSelectedUser())
            dispatch(
              setAlert({
                key: 'user.update.success',
                type: AlertMessageEnum.SUCCESS,
              })
            )
          } catch (e) {
            if (e.response && e.response.status && e.response.status === 409) {
              dispatch(
                setAlert({
                  key: `user.update.fail.duplicate.${e.response.data.error.field}`,
                  type: AlertMessageEnum.ERROR,
                })
              )
            } else if (
              e.response &&
              e.response.status &&
              e.response.status === 401
            ) {
              dispatch(loadProfile())
              const respUpdate = await userApi.updateUser(selectedUser.id, {
                ...value,
                dateOfBirth: new Date(
                  value.dateOfBirth || selectedUser.dateOfBirth
                ).getTime(),
                email:
                  value.email && value.email.trim().length
                    ? value.email
                    : undefined,
              })
              replaceUsers(respUpdate.data)
              if (selectedUser.name !== value.name && isStationsLoaded) {
                dispatch(loadStations())
                if (
                  selectedUser.userType.type === UserTypeEnum.OWNER &&
                  isEmployeesLoaded
                ) {
                  dispatch(loadEmployees())
                }
              }
              dispatch(setSelectedUser())
              dispatch(
                setAlert({
                  key: 'user.update.success',
                  type: AlertMessageEnum.SUCCESS,
                })
              )
            } else {
              dispatch(
                setAlert({
                  key: 'user.update.fail.internal',
                  type: AlertMessageEnum.ERROR,
                })
              )
            }
          }
        }
        break
      case ActionEnum.CREATE:
        try {
          const respCreate = await userApi.createUser({
            ...value,
            dateOfBirth: new Date(value.dateOfBirth).getTime(),
            email:
              value.email && value.email.trim().length
                ? value.email
                : undefined,
          })
          dispatch(setUsers([respCreate.data, ...users]))
          dispatch(setSelectedUser())
          dispatch(
            setAlert({
              key: 'user.create.success',
              type: AlertMessageEnum.SUCCESS,
            })
          )
        } catch (e) {
          if (e.response && e.response.status && e.response.status === 409) {
            dispatch(
              setAlert({
                key: `user.create.fail.duplicate.${e.response.data.error.field}`,
                type: AlertMessageEnum.ERROR,
              })
            )
          } else if (
            e.response &&
            e.response.status &&
            e.response.status === 401
          ) {
            dispatch(loadProfile())
            const respCreate = await userApi.createUser({
              ...value,
              dateOfBirth: new Date(value.dateOfBirth).getTime(),
              email:
                value.email && value.email.trim().length
                  ? value.email
                  : undefined,
            })
            dispatch(setUsers([respCreate.data, ...users]))
            dispatch(setSelectedUser())
            dispatch(
              setAlert({
                key: 'user.create.success',
                type: AlertMessageEnum.SUCCESS,
              })
            )
          } else {
            dispatch(
              setAlert({
                key: 'user.create.fail.internal',
                type: AlertMessageEnum.ERROR,
              })
            )
          }
        }
        break
      case ActionEnum.DELETE:
        if (selectedUser) {
          try {
            await userApi.deleteUser(selectedUser.id)
            dispatch(
              setUsers(users.filter((item) => item.id !== selectedUser.id))
            )
            dispatch(setSelectedUser())
            dispatch(
              setAlert({
                key: 'user.delete.success',
                type: AlertMessageEnum.SUCCESS,
              })
            )
          } catch (e) {
            if (e.response && e.response.status && e.response.status === 401) {
              dispatch(loadProfile())
              await userApi.deleteUser(selectedUser.id)
              dispatch(
                setUsers(users.filter((item) => item.id !== selectedUser.id))
              )
              dispatch(setSelectedUser())
              dispatch(
                setAlert({
                  key: 'user.delete.success',
                  type: AlertMessageEnum.SUCCESS,
                })
              )
            } else {
              dispatch(
                setAlert({
                  key:
                    e.response && e.response.status && e.response.status === 400
                      ? 'common.delete.fail.fkConstraint'
                      : 'user.delete.fail.internal',
                  type: AlertMessageEnum.ERROR,
                })
              )
            }
          }
        }
        break
      default:
        break
    }
    dispatch(setActionProcessing(false))
  }

  const body = (
    <Form onFinish={onFinish} form={form}>
      {(!management.alertMessage ||
        management.alertMessage.type !== AlertMessageEnum.SUCCESS) && (
        <>
          {readOnlyMode && (
            <>
              <StyledFormItem
                label={
                  <StyledLabel>{translate('user.name', language)}</StyledLabel>
                }
              >
                {selectedUser?.name}
              </StyledFormItem>
              <StyledFormItem
                label={
                  <StyledLabel>
                    {translate('user.address', language)}
                  </StyledLabel>
                }
              >
                {selectedUser?.address}
              </StyledFormItem>
              <StyledFormItem
                label={
                  <StyledLabel>
                    {translate('common.phone', language)}
                  </StyledLabel>
                }
              >
                {selectedUser?.phone}
              </StyledFormItem>
              <StyledFormItem
                label={
                  <StyledLabel>
                    {translate('common.identityCardNumber', language)}
                  </StyledLabel>
                }
              >
                {selectedUser?.identityCardNumber}
              </StyledFormItem>
              <StyledFormItem
                label={
                  <StyledLabel>
                    {translate('user.username', language)}
                  </StyledLabel>
                }
              >
                {selectedUser?.username}
              </StyledFormItem>
              <StyledFormItem
                label={
                  <StyledLabel>
                    {translate('common.dateOfBirth', language)}
                  </StyledLabel>
                }
              >
                {getDateFormat(selectedUser?.dateOfBirth || 0)}
              </StyledFormItem>
              <StyledFormItem
                label={
                  <StyledLabel>
                    {translate('common.gender', language)}
                  </StyledLabel>
                }
              >
                {translate(
                  `common.gender.${selectedUser?.gender ? 'male' : 'female'}`,
                  language
                )}
              </StyledFormItem>
              <StyledFormItem
                label={
                  <StyledLabel>{translate('user.email', language)}</StyledLabel>
                }
              >
                {selectedUser?.email}
              </StyledFormItem>
              <StyledFormItem
                label={
                  <StyledLabel>
                    {translate('common.note', language)}
                  </StyledLabel>
                }
              >
                {selectedUser?.note}
              </StyledFormItem>
            </>
          )}
          {!readOnlyMode && (
            <>
              <StyledFormItem
                label={
                  <StyledLabel>{translate('user.name', language)}</StyledLabel>
                }
                name="name"
                initialValue={selectedUser?.name}
                rules={[
                  {
                    required: true,
                    message: '',
                  },
                  () => ({
                    validator(_, value) {
                      if (!value || !value.trim().length) {
                        return Promise.reject(
                          new Error(translate('user.invalid.name', language))
                        )
                      }
                      return Promise.resolve()
                    },
                  }),
                ]}
              >
                <Input
                  placeholder={translate('user.name', language)}
                  allowClear
                />
              </StyledFormItem>
              <StyledFormItem
                name="address"
                label={
                  <StyledLabel>
                    {translate('user.address', language)}
                  </StyledLabel>
                }
                initialValue={selectedUser?.address}
                rules={[
                  {
                    required: true,
                    message: '',
                  },
                  () => ({
                    validator(_, value) {
                      if (!value || !value.trim().length) {
                        return Promise.reject(
                          new Error(translate('user.invalid.address', language))
                        )
                      }
                      return Promise.resolve()
                    },
                  }),
                ]}
              >
                <Input
                  placeholder={translate('user.address', language)}
                  allowClear
                />
              </StyledFormItem>
              <StyledFormItem
                name="phone"
                label={
                  <StyledLabel>
                    {translate('common.phone', language)}
                  </StyledLabel>
                }
                initialValue={selectedUser?.phone}
                rules={[
                  {
                    required: true,
                    message: translate('user.invalid.phone', language),
                    pattern: regex.phone,
                  },
                ]}
              >
                <Input
                  placeholder={translate('common.phone', language)}
                  allowClear
                />
              </StyledFormItem>
              <StyledFormItem
                name="identityCardNumber"
                label={
                  <StyledLabel>
                    {translate('common.identityCardNumber', language)}
                  </StyledLabel>
                }
                initialValue={selectedUser?.identityCardNumber}
                rules={[
                  {
                    required: true,
                    message: translate(
                      'common.invalid.identityCardNumber',
                      language
                    ),
                    pattern: regex.identityCardNumber,
                  },
                ]}
              >
                <Input
                  placeholder={translate('common.identityCardNumber', language)}
                  allowClear
                />
              </StyledFormItem>
              {management.action === ActionEnum.CREATE && (
                <>
                  <StyledFormItem
                    name="username"
                    label={
                      <StyledLabel>
                        {translate('user.username', language)}
                      </StyledLabel>
                    }
                    initialValue={selectedUser?.username}
                    rules={[
                      {
                        required: true,
                        message: translate('user.invalid.username', language),
                        pattern: new RegExp(/^[a-zA-Z0-9]{3,}$/),
                      },
                    ]}
                  >
                    <Input
                      placeholder={translate('user.username', language)}
                      allowClear
                    />
                  </StyledFormItem>
                  <StyledFormItem
                    name="password"
                    label={
                      <StyledLabel>
                        {translate('user.password', language)}
                      </StyledLabel>
                    }
                    rules={[
                      {
                        required: true,
                        message: translate('user.invalid.password', language),
                        pattern: new RegExp(
                          /^(?=.*[0-9])(?=.*[a-zA-Z])([a-zA-Z0-9]+){8,}$/
                        ),
                      },
                    ]}
                  >
                    <Input.Password
                      placeholder={translate('user.password', language)}
                      allowClear
                    />
                  </StyledFormItem>
                  <StyledFormItem
                    name="confirmPassword"
                    label={
                      <span style={{ fontWeight: 'bold' }}>
                        {translate('user.confirmPassword', language)}
                      </span>
                    }
                    dependencies={['password']}
                    rules={[
                      {
                        required: true,
                        message: '',
                      },
                      ({ getFieldValue }) => ({
                        validator(_, value) {
                          if (getFieldValue('password') === value) {
                            return Promise.resolve()
                          }
                          return Promise.reject(
                            new Error(
                              translate(
                                'user.invalid.confirmPassword',
                                language
                              )
                            )
                          )
                        },
                      }),
                    ]}
                  >
                    <Input.Password allowClear />
                  </StyledFormItem>
                </>
              )}
              <StyledFormItem
                label={
                  <StyledLabel>
                    {translate('common.gender', language)}
                  </StyledLabel>
                }
                name="gender"
                initialValue={selectedUser?.gender}
                rules={[
                  {
                    required: true,
                    message: translate('user.required.gender', language),
                  },
                ]}
              >
                <Radio.Group>
                  {[true, false].map((item) => {
                    return (
                      <Radio value={item} key={item.toString()}>
                        {translate(
                          `common.gender.${item ? 'male' : 'female'}`,
                          language
                        )}
                      </Radio>
                    )
                  })}
                </Radio.Group>
              </StyledFormItem>
              <StyledFormItem
                label={
                  <StyledLabel>
                    {translate('common.dateOfBirth', language)}
                  </StyledLabel>
                }
                name="dateOfBirth"
                initialValue={moment(
                  selectedUser?.dateOfBirth || new Date('1970-01-01')
                )}
                rules={[
                  {
                    required: true,
                    message: '',
                  },
                  () => ({
                    validator(_, value) {
                      if (
                        !value ||
                        value.toDate().getTime() >= new Date().getTime()
                      ) {
                        return Promise.reject(
                          new Error(
                            translate('common.invalid.dateOfBirth', language)
                          )
                        )
                      }
                      return Promise.resolve()
                    },
                  }),
                ]}
              >
                <DatePicker
                  placeholder={translate('common.dateOfBirth', language)}
                  format="YYYY-MM-DD"
                />
              </StyledFormItem>
              <StyledFormItem
                name="email"
                label={
                  <StyledLabel>{translate('user.email', language)}</StyledLabel>
                }
                initialValue={selectedUser?.email}
                rules={[
                  {
                    message: translate('user.invalid.email', language),
                    pattern: new RegExp(/^[a-zA-Z0-9]+@(\w+\.)*(\w+)$/),
                  },
                ]}
              >
                <Input
                  placeholder={translate('user.email', language)}
                  allowClear
                />
              </StyledFormItem>
              <StyledFormItem
                name="note"
                label={
                  <StyledLabel>
                    {translate('common.note', language)}
                  </StyledLabel>
                }
                initialValue={selectedUser?.note}
              >
                <Input
                  placeholder={translate('common.note', language)}
                  allowClear
                />
              </StyledFormItem>
            </>
          )}
          {(readOnlyMode || management.action === ActionEnum.UPDATE) && (
            <StyledFormItem
              label={
                <StyledLabel>
                  {translate('user.userType', language)}
                </StyledLabel>
              }
            >
              <Tag
                style={{ fontSize: '0.7rem' }}
                color={
                  selectedUser?.userType.type === UserTypeEnum.ADMIN
                    ? 'green'
                    : selectedUser?.userType.type === UserTypeEnum.CUSTOMER
                    ? 'blue'
                    : 'red'
                }
              >
                {translate(
                  `user.userType.${selectedUser?.userType.type.toLowerCase()}`,
                  language
                )}
              </Tag>
            </StyledFormItem>
          )}
          {management.action === ActionEnum.CREATE && (
            <StyledFormItem
              name="userTypeId"
              label={
                <StyledLabel>
                  {translate('user.userType', language)}
                </StyledLabel>
              }
              initialValue={selectedUser?.userType.id}
              rules={[
                {
                  required: true,
                  message: translate('user.required.userType', language),
                },
              ]}
            >
              <Select
                style={{ width: '100%' }}
                loading={!isUserTypesLoaded}
                disabled={!isUserTypesLoaded}
              >
                {userTypes
                  .filter((item) =>
                    profile && profile.userType.type === UserTypeEnum.ADMIN
                      ? item.type !== UserTypeEnum.ADMIN
                      : item.type === UserTypeEnum.CUSTOMER
                  )
                  .map((item) => (
                    <Select.Option value={item.id} key={item.id}>
                      {item.type}
                    </Select.Option>
                  ))}
              </Select>
            </StyledFormItem>
          )}
        </>
      )}
      <ManageModalFooterComponent
        hasDefaultButtons
        onDeleteConfirm={() => form.submit()}
        customButtons={
          <>
            {management.action === ActionEnum.UPDATE &&
              (!management.alertMessage ||
                management.alertMessage.type !== AlertMessageEnum.SUCCESS) && (
                <Button
                  type="ghost"
                  style={{
                    outline: 'none',
                    marginRight: '10px',
                  }}
                  disabled={management.actionProcessing}
                  onClick={() => {
                    dispatch(setShowManageModal(false))
                    dispatch(setShowResetPasswordModal(true))
                  }}
                >
                  {translate('user.resetPassword', language)}
                </Button>
              )}
          </>
        }
      />
    </Form>
  )

  return (
    <ManageModalComponent
      body={body}
      width={isDesktopScreen ? '40%' : '100%'}
    />
  )
}
